import { useThemeStore } from '@/stores/themeStore'

/**
 * The one place a chart's colour is decided (§61).
 *
 * Before this module the interface covered grid/axis/tick/tooltip and stopped —
 * there was no slot for the categorical series order, the ordinal ramp or the
 * lifecycle status scale, so six files declared private `CHART_*` constants and
 * two `PIPELINE_COLORS` arrays drifted apart. The visible damage was that a
 * series' hue was not stable per entity: "replied" was emerald in Overview and
 * violet in DrillDown, "opened" was indigo in Overview and emerald in
 * DrillDown — so a reader who learned "green = replies" read green as "opened"
 * one click later.
 *
 * Recharts renders SVG `<line>` / `<text>` that does not always pick up
 * Tailwind className tokens — every value here is an explicit hex / rgba string.
 */
export interface ChartTheme {
  /** Grid line stroke (light dashed gridlines on plot area) */
  grid: string
  /** Axis spine + tick stroke (slightly stronger than grid) */
  axis: string
  /** Tick label fill (muted body text) */
  tickFill: string
  /** Tooltip card background */
  tooltipBg: string
  /** Tooltip card border */
  tooltipBorder: string
  /** Tooltip text */
  tooltipText: string
  /**
   * Ordinal ramp for a funnel / pipeline, index 0 = top of funnel. Six steps of
   * one indigo family — hierarchy is carried by bar length, the ramp only fades
   * with attrition. **Theme-dependent by necessity:** a single ramp cannot clear
   * 3:1 against both a white card and a `#191a1b` one, so the light ramp fades
   * *up* toward white and the dark ramp fades *down* toward the surface, and
   * every step in each clears 3:1 on its own background.
   */
  sequential: string[]
}

const LIGHT: ChartTheme = {
  grid:          'rgba(15, 16, 17, 0.06)',
  axis:          'rgba(15, 16, 17, 0.18)',
  tickFill:      '#5f6166',
  tooltipBg:     '#ffffff',
  tooltipBorder: 'rgba(15, 16, 17, 0.08)',
  tooltipText:   '#0f1011',
  // 10.0:1 → 3.3:1 on white. `#5e6ad2` (brand, 4.7:1) sits at index 3; the
  // original ramp's last three steps were 1.2–1.7:1 and are gone.
  sequential: ['#333b83', '#414a9e', '#4f59b8', '#5e6ad2', '#6d78d8', '#7c87dd'],
}

const DARK: ChartTheme = {
  grid:          'rgba(255, 255, 255, 0.06)',
  axis:          'rgba(255, 255, 255, 0.18)',
  tickFill:      '#8a8f98',
  tooltipBg:     '#191a1b',
  tooltipBorder: 'rgba(255, 255, 255, 0.08)',
  tooltipText:   '#f7f8f8',
  // 12.5:1 → 3.7:1 on the `#191a1b` card. Brand `#5e6ad2` is the *faintest*
  // step here, not the strongest — on a near-black surface fading means
  // darkening.
  sequential: ['#c7cbf0', '#b0b6ea', '#9aa3e8', '#8590e2', '#7079db', '#5e6ad2'],
}

export function useChartTheme(): ChartTheme {
  const actual = useThemeStore((s) => s.actualTheme)
  return actual === 'dark' ? DARK : LIGHT
}

export function tooltipContentStyle(t: ChartTheme): React.CSSProperties {
  return {
    backgroundColor: t.tooltipBg,
    border: `1px solid ${t.tooltipBorder}`,
    borderRadius: '8px',
    fontSize: '12px',
    color: t.tooltipText,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  }
}

/**
 * Picks the ordinal step for item `index` of `count`, spread across the whole
 * ramp. Indexing the ramp directly would leave a three-stage funnel using only
 * its dark end, which reads as "these three stages are nearly the same".
 */
export function sequentialStep(t: ChartTheme, index: number, count: number): string {
  const ramp = t.sequential
  if (count <= 1) return ramp[0]
  const pos = Math.round((index / (count - 1)) * (ramp.length - 1))
  return ramp[Math.min(Math.max(pos, 0), ramp.length - 1)]
}

/**
 * The email-lifecycle series scale — **one hue per metric, everywhere**.
 *
 * Values are `DeliverabilityTab`'s former `STATUS_BAR_COLORS` verbatim, which
 * is the one scale in the app that passed every CVD gate (adjacent ΔE 12.0
 * deutan / 16.0 tritan, 21.6 normal vision). It is the anchor rather than one
 * of the drifted copies precisely because it was measured.
 *
 * `sent` is the only added key: it is the denominator every other metric is a
 * share of, so it takes the quaternary gray — a baseline, not a category — and
 * stays clear of `unsubscribed`'s lighter `#8a8f98`.
 */
export type SeriesKey =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'replied'
  | 'bounced'
  | 'unsubscribed'

export const SERIES_COLORS: Record<SeriesKey, string> = {
  sent:         '#62666d', // quaternary — the baseline all other rates divide
  delivered:    '#10b981', // emerald
  opened:       '#5e6ad2', // indigo (brand)
  clicked:      '#f5a524', // amber — admitted as a data hue by the §61 ruling
  replied:      '#7170ff', // violet
  bounced:      '#ef4444', // red
  unsubscribed: '#8a8f98', // tertiary
}

/**
 * Dash patterns carry the encoding a second way, for the pairs colour alone
 * cannot separate: `opened` indigo ↔ `replied` violet is ΔE 7.4, and `sent`
 * ↔ `unsubscribed` are two grays. Line charts should pass `strokeDasharray`
 * from here; bars and dots ignore it.
 */
export const SERIES_DASH: Partial<Record<SeriesKey, string>> = {
  replied:      '6 3',
  unsubscribed: '2 3',
}

/**
 * Backend field names for the same metric are not consistent — the overview
 * trend sends `opens`/`clicks`/`replies`, the campaign timeline sends
 * `opened`/`clicked`/`replied`, the health scores send `open_rate`. They are
 * one series each, so they resolve to one hue each.
 */
const SERIES_ALIASES: Record<string, SeriesKey> = {
  emails_sent:         'sent',
  sends:               'sent',
  delivery_rate:       'delivered',
  delivers:            'delivered',
  opens:               'opened',
  open:                'opened',
  open_rate:           'opened',
  clicks:              'clicked',
  click:               'clicked',
  click_rate:          'clicked',
  replies:             'replied',
  reply:               'replied',
  reply_rate:          'replied',
  total_replies:       'replied',
  bounces:             'bounced',
  bounce:              'bounced',
  bounce_rate:         'bounced',
  unsubscribes:        'unsubscribed',
  unsubscribe:         'unsubscribed',
  unsubscribe_rate:    'unsubscribed',
  spam_complaint_rate: 'bounced',
}

function resolveSeries(key: string): SeriesKey | null {
  const k = key.toLowerCase().trim()
  if (k in SERIES_COLORS) return k as SeriesKey
  return SERIES_ALIASES[k] ?? null
}

/** Series hue for a metric key or one of its aliases. Unknown keys fall back to the baseline gray. */
export function seriesColor(key: string): string {
  const resolved = resolveSeries(key)
  return resolved ? SERIES_COLORS[resolved] : SERIES_COLORS.sent
}

/** Dash pattern for a metric key, or `undefined` for a solid stroke. */
export function seriesDash(key: string): string | undefined {
  const resolved = resolveSeries(key)
  return resolved ? SERIES_DASH[resolved] : undefined
}

/**
 * The hue for a chart plotting **one** measure that is not part of the email
 * lifecycle — contact totals over time, for instance. Brand indigo, identical in
 * both modes per §3, so a single-series chart never has to invent a colour or
 * borrow `opened`'s.
 */
export const BRAND_SERIES_COLOR = '#5e6ad2'

/**
 * Engagement level is **ordinal with valence** — "highly engaged" is both the
 * top of a scale and good news — so it keeps a valence scale rather than the
 * ordinal ramp, which would say nothing about `never_engaged` being the bad
 * end. Same four tones §31 uses for chips.
 */
export const ENGAGEMENT_COLORS: Record<string, string> = {
  highly_engaged:     '#10b981', // emerald — success valence
  moderately_engaged: '#5e6ad2', // indigo — info valence
  low_engagement:     '#f5a524', // amber — warning valence
  never_engaged:      '#ef4444', // red — destructive valence
}

/** Fallback for an engagement level the API adds later. Never borrows another level's hue. */
export const ENGAGEMENT_FALLBACK = '#8a8f98'
